import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, Users, FileText, Home, Euro, Plus, Settings, 
  MoreHorizontal, Eye, Edit, Trash2, Shield, Activity, TrendingUp,
  ExternalLink, Zap, Package, Sparkles, Calendar, ToggleLeft, ToggleRight, Palette, Loader2, Check
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PlatformAdmin = () => {
  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewCompany, setViewCompany] = useState(null);
  const [creating, setCreating] = useState(false);
  
  // Company configuration state
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [configCompany, setConfigCompany] = useState(null);
  const [companyConfig, setCompanyConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [presets, setPresets] = useState([]);
  
  const { user, isSuperAdmin, enterCompanyCRM, viewingCompany, exitCompanyCRM } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    owner_email: '',
    owner_password: '',
    owner_first_name: '',
    owner_last_name: '',
    email: '',
    phone: '',
    address_line1: '',
    address_line2: '',
    vat_number: ''
  });

  useEffect(() => {
    // If viewing a company, redirect to dashboard
    if (viewingCompany) {
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [viewingCompany, navigate]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const [statsRes, companiesRes] = await Promise.all([
        fetch(`${API_URL}/api/platform/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/platform/companies`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      if (statsRes.ok) {
        setStats(await statsRes.json());
      }
      if (companiesRes.ok) {
        setCompanies(await companiesRes.json());
      }
    } catch (error) {
      console.error('Error fetching platform data:', error);
      toast.error('Kon platform data niet laden');
    } finally {
      setLoading(false);
    }
  };

  const handleEnterCompanyCRM = (company) => {
    enterCompanyCRM(company);
    toast.success(`Je beheert nu: ${company.name}`);
    navigate('/dashboard');
  };

  const handleCreateCompany = async (e) => {
    e.preventDefault();
    setCreating(true);
    
    try {
      const token = localStorage.getItem('token');
      
      const params = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      
      const response = await fetch(`${API_URL}/api/platform/companies?${params}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(`Bedrijf "${formData.name}" aangemaakt!`);
        setCreateDialogOpen(false);
        setFormData({
          name: '', slug: '', owner_email: '', owner_password: '',
          owner_first_name: '', owner_last_name: '', email: '',
          phone: '', address_line1: '', address_line2: '', vat_number: ''
        });
        fetchData();
      } else {
        toast.error(data.detail || 'Kon bedrijf niet aanmaken');
      }
    } catch (error) {
      toast.error('Fout bij aanmaken bedrijf');
    } finally {
      setCreating(false);
    }
  };

  const handleViewCompany = async (companyId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/platform/companies/${companyId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        setViewCompany(await response.json());
      }
    } catch (error) {
      toast.error('Kon bedrijfsdetails niet laden');
    }
  };

  const handleUpdateStatus = async (companyId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/platform/companies/${companyId}?status=${newStatus}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Status bijgewerkt');
        fetchData();
        if (viewCompany?.id === companyId) {
          handleViewCompany(companyId);
        }
      }
    } catch (error) {
      toast.error('Kon status niet bijwerken');
    }
  };

  // Configuration functions
  const fetchPresets = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/platform/presets`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPresets(data.presets || []);
      }
    } catch (error) {
      console.error('Error fetching presets:', error);
    }
  };

  const handleOpenConfig = async (company) => {
    setConfigCompany(company);
    setConfigDialogOpen(true);
    setConfigLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/platform/companies/${company.id}/config`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCompanyConfig(data);
      }
    } catch (error) {
      toast.error('Kon configuratie niet laden');
    } finally {
      setConfigLoading(false);
    }
    
    // Fetch presets if not loaded
    if (presets.length === 0) {
      fetchPresets();
    }
  };

  const handleToggleModule = async (moduleKey, enabled) => {
    if (!configCompany || !companyConfig) return;
    
    // Optimistic update
    const updatedModules = {
      ...companyConfig.modules_config,
      [moduleKey]: {
        ...companyConfig.modules_config[moduleKey],
        enabled
      }
    };
    setCompanyConfig({ ...companyConfig, modules_config: updatedModules });
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/platform/companies/${configCompany.id}/modules/${moduleKey}?enabled=${enabled}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        toast.success(`Module ${enabled ? 'geactiveerd' : 'gedeactiveerd'}`);
      } else {
        // Revert on error
        toast.error('Kon module niet bijwerken');
        setCompanyConfig({ ...companyConfig });
      }
    } catch (error) {
      toast.error('Fout bij bijwerken module');
    }
  };

  const handleApplyPreset = async (presetId) => {
    if (!configCompany) return;
    
    setSavingConfig(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/platform/companies/${configCompany.id}/apply-preset?preset=${presetId}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Preset "${presetId}" toegepast!`);
        // Refresh config
        handleOpenConfig(configCompany);
      } else {
        toast.error('Kon preset niet toepassen');
      }
    } catch (error) {
      toast.error('Fout bij toepassen preset');
    } finally {
      setSavingConfig(false);
    }
  };

  const handleUpdateModuleName = async (moduleKey, newName) => {
    if (!configCompany || !companyConfig) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/platform/companies/${configCompany.id}/modules/${moduleKey}?enabled=true&display_name=${encodeURIComponent(newName)}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        toast.success('Naam bijgewerkt');
        // Update local state
        const updatedModules = {
          ...companyConfig.modules_config,
          [moduleKey]: {
            ...companyConfig.modules_config[moduleKey],
            display_name: newName
          }
        };
        setCompanyConfig({ ...companyConfig, modules_config: updatedModules });
      }
    } catch (error) {
      toast.error('Fout bij bijwerken naam');
    }
  };

  const handleUpdateBranding = async (key, value) => {
    if (!configCompany || !companyConfig) return;
    
    // Optimistic update
    const updatedBranding = {
      ...companyConfig.branding,
      [key]: value
    };
    setCompanyConfig({ ...companyConfig, branding: updatedBranding });
    
    // Debounced save (wait for user to stop typing)
    if (window.brandingTimeout) {
      clearTimeout(window.brandingTimeout);
    }
    
    window.brandingTimeout = setTimeout(async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(
          `${API_URL}/api/platform/companies/${configCompany.id}/config`,
          {
            method: 'POST',
            headers: { 
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ branding: updatedBranding })
          }
        );
        
        if (response.ok) {
          toast.success('Branding bijgewerkt');
        } else {
          toast.error('Kon branding niet opslaan');
        }
      } catch (error) {
        toast.error('Fout bij bijwerken branding');
      }
    }, 500);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-500/20 text-green-400">Actief</Badge>;
      case 'trial':
        return <Badge className="bg-blue-500/20 text-blue-400">Proefperiode</Badge>;
      case 'suspended':
        return <Badge className="bg-red-500/20 text-red-400">Opgeschort</Badge>;
      case 'cancelled':
        return <Badge variant="secondary">Geannuleerd</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Check if user is super admin
  if (!isSuperAdmin) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <Shield className="w-16 h-16 text-muted-foreground" />
        <h2 className="text-xl font-semibold">Geen Toegang</h2>
        <p className="text-muted-foreground">Je hebt Super Admin rechten nodig om deze pagina te bekijken.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="platform-admin-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
            <Zap className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">Business Assistant</h1>
            <p className="text-muted-foreground">Platform Beheer - Beheer alle bedrijven</p>
          </div>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)} data-testid="create-company-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nieuw Bedrijf
        </Button>
      </div>

      {/* Platform Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Bedrijven</p>
                  <p className="text-2xl font-bold">{stats.companies.total}</p>
                  <p className="text-xs text-green-500">{stats.companies.active} actief</p>
                </div>
                <Building2 className="w-8 h-8 text-primary opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Gebruikers</p>
                  <p className="text-2xl font-bold">{stats.users.total}</p>
                  <p className="text-xs text-muted-foreground">{stats.users.super_admins} super admin</p>
                </div>
                <Users className="w-8 h-8 text-blue-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Klanten</p>
                  <p className="text-2xl font-bold">{stats.data.customers}</p>
                </div>
                <Users className="w-8 h-8 text-green-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Adressen</p>
                  <p className="text-2xl font-bold">{stats.data.properties}</p>
                </div>
                <Home className="w-8 h-8 text-orange-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Gefactureerd</p>
                  <p className="text-2xl font-bold">{formatCurrency(stats.revenue.total_invoiced)}</p>
                </div>
                <Euro className="w-8 h-8 text-yellow-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Companies List */}
      <Card>
        <CardHeader>
          <CardTitle>Bedrijven</CardTitle>
          <CardDescription>Alle geregistreerde zonnepanelenbedrijven op het platform</CardDescription>
        </CardHeader>
        <CardContent>
          {companies.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Building2 className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Nog geen bedrijven geregistreerd</p>
              <Button className="mt-4" onClick={() => setCreateDialogOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Eerste Bedrijf Aanmaken
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-3 font-medium">Bedrijf</th>
                    <th className="text-left p-3 font-medium">Status</th>
                    <th className="text-left p-3 font-medium">Gebruikers</th>
                    <th className="text-left p-3 font-medium">Klanten</th>
                    <th className="text-left p-3 font-medium">Adressen</th>
                    <th className="text-right p-3 font-medium">Acties</th>
                  </tr>
                </thead>
                <tbody>
                  {companies.map(company => (
                    <tr key={company.id} className="border-b hover:bg-muted/50">
                      <td className="p-3">
                        <p className="font-medium">{company.name}</p>
                        <p className="text-sm text-muted-foreground">{company.slug}</p>
                      </td>
                      <td className="p-3">{getStatusBadge(company.status)}</td>
                      <td className="p-3">{company.total_users}</td>
                      <td className="p-3">{company.total_customers}</td>
                      <td className="p-3">{company.total_properties}</td>
                      <td className="p-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button 
                            size="sm" 
                            onClick={() => handleEnterCompanyCRM(company)}
                            className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700"
                            data-testid={`manage-crm-${company.slug}`}
                          >
                            <ExternalLink className="w-4 h-4 mr-1" />
                            Beheer CRM
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleViewCompany(company.id)}>
                                <Eye className="w-4 h-4 mr-2" />
                                Details Bekijken
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleOpenConfig(company)}>
                                <Settings className="w-4 h-4 mr-2" />
                                Configuratie
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => handleUpdateStatus(company.id, 'active')}>
                                Activeren
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleUpdateStatus(company.id, 'suspended')}>
                                Opschorten
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Company Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Nieuw Bedrijf Aanmaken</DialogTitle>
            <DialogDescription>
              Registreer een nieuw zonnepanelenbedrijf op het platform
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleCreateCompany} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Bedrijfsnaam *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Solar Company BV"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Slug (URL) *</Label>
                <Input
                  value={formData.slug}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-')
                  }))}
                  placeholder="solar-company"
                  required
                />
              </div>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">Eigenaar Account</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Email eigenaar *</Label>
                  <Input
                    type="email"
                    value={formData.owner_email}
                    onChange={(e) => setFormData(prev => ({ ...prev, owner_email: e.target.value }))}
                    placeholder="admin@company.be"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Wachtwoord *</Label>
                  <Input
                    type="password"
                    value={formData.owner_password}
                    onChange={(e) => setFormData(prev => ({ ...prev, owner_password: e.target.value }))}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Voornaam</Label>
                  <Input
                    value={formData.owner_first_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, owner_first_name: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Achternaam</Label>
                  <Input
                    value={formData.owner_last_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, owner_last_name: e.target.value }))}
                  />
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">Bedrijfsgegevens</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Email bedrijf</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="info@company.be"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Telefoon</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Adres regel 1</Label>
                  <Input
                    value={formData.address_line1}
                    onChange={(e) => setFormData(prev => ({ ...prev, address_line1: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Adres regel 2</Label>
                  <Input
                    value={formData.address_line2}
                    onChange={(e) => setFormData(prev => ({ ...prev, address_line2: e.target.value }))}
                  />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label>BTW nummer</Label>
                  <Input
                    value={formData.vat_number}
                    onChange={(e) => setFormData(prev => ({ ...prev, vat_number: e.target.value }))}
                    placeholder="BE 0123.456.789"
                  />
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                Annuleren
              </Button>
              <Button type="submit" disabled={creating}>
                {creating ? 'Aanmaken...' : 'Bedrijf Aanmaken'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Company Dialog */}
      <Dialog open={!!viewCompany} onOpenChange={() => setViewCompany(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          {viewCompany && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  {viewCompany.name}
                </DialogTitle>
                <DialogDescription>
                  {viewCompany.slug} • {getStatusBadge(viewCompany.status)}
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-6">
                {/* Stats Grid */}
                <div className="grid grid-cols-5 gap-4">
                  <Card>
                    <CardContent className="pt-4 text-center">
                      <p className="text-2xl font-bold">{viewCompany.stats?.total_users || 0}</p>
                      <p className="text-xs text-muted-foreground">Gebruikers</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4 text-center">
                      <p className="text-2xl font-bold">{viewCompany.stats?.total_customers || 0}</p>
                      <p className="text-xs text-muted-foreground">Klanten</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4 text-center">
                      <p className="text-2xl font-bold">{viewCompany.stats?.total_properties || 0}</p>
                      <p className="text-xs text-muted-foreground">Adressen</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4 text-center">
                      <p className="text-2xl font-bold">{viewCompany.stats?.total_offertes || 0}</p>
                      <p className="text-xs text-muted-foreground">Offertes</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4 text-center">
                      <p className="text-2xl font-bold">{viewCompany.stats?.total_facturen || 0}</p>
                      <p className="text-xs text-muted-foreground">Facturen</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Company Details */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-muted-foreground">Email</Label>
                    <p>{viewCompany.email || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Telefoon</Label>
                    <p>{viewCompany.phone || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Adres</Label>
                    <p>{viewCompany.address_line1 || '-'}</p>
                    <p>{viewCompany.address_line2 || ''}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">BTW nummer</Label>
                    <p>{viewCompany.vat_number || '-'}</p>
                  </div>
                </div>

                {/* Users List */}
                <div>
                  <h4 className="font-medium mb-2">Gebruikers ({viewCompany.users?.length || 0})</h4>
                  <div className="space-y-2">
                    {viewCompany.users?.slice(0, 10).map(u => (
                      <div key={u.id} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                        <div>
                          <p className="font-medium">{u.first_name} {u.last_name}</p>
                          <p className="text-sm text-muted-foreground">{u.email}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{u.role}</Badge>
                          {u.platform_role && (
                            <Badge className="bg-purple-500/20 text-purple-400">{u.platform_role}</Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Company Configuration Dialog */}
      <Dialog open={configDialogOpen} onOpenChange={setConfigDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Configuratie: {configCompany?.name}
            </DialogTitle>
            <DialogDescription>
              Pas de modules en instellingen aan voor dit bedrijf
            </DialogDescription>
          </DialogHeader>
          
          {configLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : companyConfig ? (
            <Tabs defaultValue="modules" className="w-full">
              <TabsList className="w-full grid grid-cols-3">
                <TabsTrigger value="modules">Modules</TabsTrigger>
                <TabsTrigger value="presets">Industry Presets</TabsTrigger>
                <TabsTrigger value="branding">Branding</TabsTrigger>
              </TabsList>
              
              {/* Modules Tab */}
              <TabsContent value="modules" className="space-y-4 mt-4">
                <p className="text-sm text-muted-foreground">
                  Schakel modules in of uit voor dit bedrijf. Je kunt ook de weergavenaam aanpassen.
                </p>
                
                <div className="space-y-3">
                  {companyConfig.modules_config && Object.entries(companyConfig.modules_config).map(([key, module]) => (
                    <div 
                      key={key} 
                      className={`p-4 border rounded-lg transition-colors ${
                        module.enabled ? 'bg-green-500/5 border-green-500/20' : 'bg-muted/30'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${module.enabled ? 'bg-green-500/10' : 'bg-muted'}`}>
                            {key === 'reinigingen' && <Sparkles className={`w-5 h-5 ${module.enabled ? 'text-green-500' : 'text-muted-foreground'}`} />}
                            {key === 'installaties' && <Zap className={`w-5 h-5 ${module.enabled ? 'text-green-500' : 'text-muted-foreground'}`} />}
                            {key === 'stock' && <Package className={`w-5 h-5 ${module.enabled ? 'text-green-500' : 'text-muted-foreground'}`} />}
                            {key === 'agenda' && <Calendar className={`w-5 h-5 ${module.enabled ? 'text-green-500' : 'text-muted-foreground'}`} />}
                            {key === 'facturen' && <FileText className={`w-5 h-5 ${module.enabled ? 'text-green-500' : 'text-muted-foreground'}`} />}
                            {key === 'offertes' && <FileText className={`w-5 h-5 ${module.enabled ? 'text-green-500' : 'text-muted-foreground'}`} />}
                          </div>
                          <div>
                            <Input 
                              value={module.display_name}
                              onChange={(e) => handleUpdateModuleName(key, e.target.value)}
                              className="h-8 w-40 font-medium"
                              disabled={!module.enabled}
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                              {module.description || `Module key: ${key}`}
                            </p>
                          </div>
                        </div>
                        <Switch 
                          checked={module.enabled}
                          onCheckedChange={(checked) => handleToggleModule(key, checked)}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>
              
              {/* Presets Tab */}
              <TabsContent value="presets" className="space-y-4 mt-4">
                <p className="text-sm text-muted-foreground">
                  Kies een industry preset om snel de juiste modules en velden in te stellen.
                </p>
                
                <div className="grid grid-cols-2 gap-3">
                  {presets.map((preset) => (
                    <div 
                      key={preset.id}
                      className={`p-4 border rounded-lg cursor-pointer hover:border-primary transition-colors ${
                        companyConfig.industry === preset.id ? 'border-primary bg-primary/5' : ''
                      }`}
                      onClick={() => handleApplyPreset(preset.id)}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium">{preset.name}</h4>
                        {companyConfig.industry === preset.id && (
                          <Check className="w-4 h-4 text-primary" />
                        )}
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(preset.modules || {}).filter(([_, m]) => m.enabled).map(([key, m]) => (
                          <Badge key={key} variant="secondary" className="text-xs">
                            {m.display_name}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                
                {savingConfig && (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    <span>Preset toepassen...</span>
                  </div>
                )}
              </TabsContent>
              
              {/* Branding Tab */}
              <TabsContent value="branding" className="space-y-4 mt-4">
                <p className="text-sm text-muted-foreground">
                  Pas de visuele stijl aan voor dit bedrijf.
                </p>
                
                <div className="space-y-6">
                  {/* Primary Color */}
                  <div className="space-y-2">
                    <Label>Primaire kleur</Label>
                    <div className="flex items-center gap-4">
                      <input
                        type="color"
                        value={companyConfig?.branding?.primary_color || '#f97316'}
                        onChange={(e) => handleUpdateBranding('primary_color', e.target.value)}
                        className="w-16 h-10 rounded cursor-pointer border"
                      />
                      <Input
                        value={companyConfig?.branding?.primary_color || '#f97316'}
                        onChange={(e) => handleUpdateBranding('primary_color', e.target.value)}
                        placeholder="#f97316"
                        className="w-28 font-mono text-sm"
                      />
                      <div className="flex gap-2">
                        {['#f97316', '#3b82f6', '#10b981', '#8b5cf6', '#ef4444', '#06b6d4'].map(color => (
                          <button
                            key={color}
                            className="w-8 h-8 rounded-full border-2 border-transparent hover:border-foreground/50 transition-colors"
                            style={{ backgroundColor: color }}
                            onClick={() => handleUpdateBranding('primary_color', color)}
                            title={color}
                          />
                        ))}
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Deze kleur wordt gebruikt voor knoppen, links en accenten.
                    </p>
                  </div>

                  {/* Logo URL */}
                  <div className="space-y-2">
                    <Label>Logo URL</Label>
                    <Input
                      value={companyConfig?.branding?.logo_url || ''}
                      onChange={(e) => handleUpdateBranding('logo_url', e.target.value)}
                      placeholder="https://example.com/logo.png"
                    />
                    <p className="text-xs text-muted-foreground">
                      URL naar het bedrijfslogo (aanbevolen: 200x50px, PNG of SVG)
                    </p>
                    {companyConfig?.branding?.logo_url && (
                      <div className="mt-2 p-4 border rounded-lg bg-muted/30">
                        <p className="text-xs text-muted-foreground mb-2">Preview:</p>
                        <img 
                          src={companyConfig.branding.logo_url} 
                          alt="Logo preview" 
                          className="max-h-12 object-contain"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                      </div>
                    )}
                  </div>

                  {/* Preview */}
                  <div className="p-4 border rounded-lg">
                    <p className="text-sm font-medium mb-3">Preview</p>
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-card border">
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: companyConfig?.branding?.primary_color || '#f97316' }}
                      >
                        <Zap className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold">{configCompany?.name}</p>
                        <p className="text-xs text-muted-foreground">Business Assistant</p>
                      </div>
                    </div>
                    <div className="mt-3 flex gap-2">
                      <Button 
                        size="sm"
                        style={{ 
                          backgroundColor: companyConfig?.branding?.primary_color || '#f97316',
                          color: 'white'
                        }}
                      >
                        Primaire knop
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        style={{ 
                          borderColor: companyConfig?.branding?.primary_color || '#f97316',
                          color: companyConfig?.branding?.primary_color || '#f97316'
                        }}
                      >
                        Outline knop
                      </Button>
                    </div>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          ) : (
            <p className="text-muted-foreground">Kon configuratie niet laden</p>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfigDialogOpen(false)}>
              Sluiten
            </Button>
            <Button onClick={() => handleEnterCompanyCRM(configCompany)}>
              <ExternalLink className="w-4 h-4 mr-2" />
              Open CRM
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PlatformAdmin;
